/**
 * The vetting pages' reads, through react-query. The cache is the one
 * source: a decision, an undo, a comment or a wording updates the payload in
 * place (`setQueryData`), and the queue and Recent refetch behind it.
 * Target payloads do not refetch on window focus: a refetch never moves the
 * page under the director, and the decision's version check (409) is what
 * keeps a stale view from being vetted.
 */
import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  VetFlagPayload,
  VetQueueHome,
  VetRecentDecision,
  VetReviewState,
  VetTargetRef,
} from '@shared/vetting/api';
import { useAuthenticatedCall } from '@/hooks/useAuthenticatedCall';
import { vetApi, type VetTargetPayload } from '@/services/vetApi';

export const vetKeys = {
  all: ['vet'] as const,
  queue: () => ['vet', 'queue'] as const,
  recent: () => ['vet', 'recent'] as const,
  targets: () => ['vet', 'target'] as const,
  targetBase: (target: VetTargetRef) =>
    ['vet', 'target', target.packId, target.kind, target.id] as const,
  target: (target: VetTargetRef, page = 0) =>
    [...vetKeys.targetBase(target), page] as const,
  flags: () => ['vet', 'flags'] as const,
  flag: (flagId: string) => ['vet', 'flag', flagId] as const,
};

export function useVetQueue() {
  const { callWithAuth } = useAuthenticatedCall();
  return useQuery({
    queryKey: vetKeys.queue(),
    queryFn: () => callWithAuth(token => vetApi.getQueue(token)),
  });
}

export function useVetRecent() {
  const { callWithAuth } = useAuthenticatedCall();
  return useQuery({
    queryKey: vetKeys.recent(),
    queryFn: () => callWithAuth(token => vetApi.getRecent(token)),
  });
}

export function useVetTargetPayload(target: VetTargetRef | null, page = 0) {
  const { callWithAuth } = useAuthenticatedCall();
  return useQuery({
    queryKey: target ? vetKeys.target(target, page) : ['vet', 'target', null],
    queryFn: () =>
      callWithAuth(token => vetApi.getTarget(target!, token, page)),
    enabled: target !== null,
    refetchOnWindowFocus: false,
    placeholderData: previous => previous,
  });
}

export function useVetFlags() {
  const { callWithAuth } = useAuthenticatedCall();
  return useQuery({
    queryKey: vetKeys.flags(),
    queryFn: () => callWithAuth(token => vetApi.listFlags(token)),
  });
}

export function useVetFlag(flagId: string) {
  const { callWithAuth } = useAuthenticatedCall();
  return useQuery({
    queryKey: vetKeys.flag(flagId),
    queryFn: () => callWithAuth(token => vetApi.getFlag(flagId, token)),
    refetchOnWindowFocus: false,
  });
}

/** The queue, Recent and the flags refetch after anything is decided or undone. */
export function refreshVetLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: vetKeys.queue() });
  void queryClient.invalidateQueries({ queryKey: vetKeys.recent() });
  void queryClient.invalidateQueries({ queryKey: vetKeys.flags() });
}

/** Writes a target's new review state into every cached page of it. */
export function patchTargetFrame(
  queryClient: QueryClient,
  target: VetTargetRef,
  patch: {
    review?: VetReviewState;
    lastDecision?: VetRecentDecision | null;
    next?: VetTargetRef | null;
  }
): void {
  queryClient.setQueriesData<VetTargetPayload>(
    { queryKey: vetKeys.targetBase(target) },
    payload => (payload ? { ...payload, ...patch } : payload)
  );
}

/** Marks the intro seen in the cached queue, so it never shows again. */
export function useMarkIntroSeenInCache() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.setQueryData<VetQueueHome>(vetKeys.queue(), queue =>
      queue ? { ...queue, introSeen: true } : queue
    );
}

export type { VetFlagPayload };
